/**
 * VH-212: tell the app where you are training.
 *
 * The reported defect was not that the app got the location wrong — it was that
 * the app never asked. A session's place was derived from the MODULE
 * (`source_template_id -> TrainingTemplate.location_id`, VH-156), which answers
 * "where is this workout normally trained", not "where am I standing". At every
 * gym that is not the module's gym the substitute picker was scoped to the
 * wrong place, and an exercise created at the real gym minutes earlier could
 * not be offered.
 *
 * This control is the athlete's answer. It is the ONLY thing that decides what
 * the pickers are scoped to, and it makes the scope visible: a list that is
 * short because of a filter must say which filter, or it reads as missing data
 * — the exact invisibility that caused this feature.
 *
 * Three deliberate properties:
 *
 * 1. **Session-local.** Changing it writes `program_sessions.location_id`, an
 *    override. It never touches the module's own location (VH-137), and never
 *    another session.
 * 2. **Reversible.** "Use the module's location" clears the override back to null, which
 *    means "nobody has said, derive it from the module" — the way back from a
 *    mistaken tap. That is why the mutation sends an explicit null rather than
 *    omitting the field.
 * 3. **Server-resolved.** The response carries the RESOLVED location, so a
 *    clear re-renders as whatever the module derives to rather than as empty.
 *    We never guess it locally.
 *
 * Authenticated-only, like VH-156's add-exercise: a QR-token holder records
 * values but does not restate where the training happened. The caller decides
 * whether to render this at all.
 */
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MapPinIcon } from '@heroicons/react/24/outline'
import { trainingLocationsAPI } from '../../services/trainingLocations'
import { weeklyProgramsAPI } from '../../services/api'
import { apiErrorMessage } from '../../utils/apiError'

export function SessionLocationControl({
  token,
  locationId,
  locationName,
  isOverride = false,
  disabled = false,
  onChanged,
}) {
  const { t } = useTranslation()
  const [error, setError] = useState(null)

  const { data: locationsData } = useQuery({
    queryKey: ['training-locations', 'list'],
    queryFn: () => trainingLocationsAPI.list().then((r) => r.data),
  })
  const locations = locationsData?.locations || []

  // The session's current place may be a location that is no longer in the
  // list — deleted, or simply not returned. Keeping it as an option means the
  // select shows the truth instead of silently snapping to the derived option,
  // which would look like the athlete had cleared it.
  const options = locations.some((l) => l.id === locationId)
    ? locations
    : locationId
      ? [...locations, { id: locationId, name: locationName || String(locationId) }]
      : locations

  const mutation = useMutation({
    mutationFn: (nextId) => weeklyProgramsAPI.setCaptureSessionLocation(token, nextId),
    onSuccess: (res) => {
      setError(null)
      onChanged?.(res?.data ?? {})
    },
    onError: (err) =>
      setError(apiErrorMessage(err, t('capture.sessionLocation.error'))),
  })

  const handleChange = (e) => {
    const raw = e.target.value
    setError(null)
    mutation.mutate(raw === '' ? null : Number(raw))
  }

  return (
    <div className="mt-3" data-testid="session-location-control">
      <label
        htmlFor="capture-session-location"
        className="mb-1 flex items-center text-xs font-medium text-gray-600"
      >
        <MapPinIcon className="mr-1 h-4 w-4 text-gray-400" />
        {t('capture.sessionLocation.label')}
      </label>
      <select
        id="capture-session-location"
        value={locationId ?? ''}
        disabled={disabled || mutation.isPending}
        onChange={handleChange}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-primary-500 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
      >
        <option value="">{t('capture.sessionLocation.useModuleLocation')}</option>
        {options.map((loc) => (
          <option key={loc.id} value={loc.id}>
            {loc.name}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500" data-testid="session-location-hint">
        {mutation.isPending
          ? t('capture.sessionLocation.saving')
          : isOverride
            ? t('capture.sessionLocation.overrideHint')
            : t('capture.sessionLocation.derivedHint')}
      </p>
      {error && (
        <p className="mt-1 text-xs text-red-600" data-testid="session-location-error">
          {error}
        </p>
      )}
    </div>
  )
}

export default SessionLocationControl
